import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
    GraphicComponent,
    GridComponent,
    LegendComponent,
    MarkAreaComponent,
    MarkLineComponent,
    TitleComponent,
    TooltipComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import type { EChartsCoreOption, ECharts } from 'echarts/core';
import { chartThemeName, registerChartTheme } from './chartTheme';
import { useChartMode } from './useChartMode';

// Modular registration — only the chart types and components the app actually
// draws are pulled in, so the bundle carries a fraction of the full echarts
// build. Add here (not via the `echarts` barrel import) when a new chart needs
// something extra.
echarts.use([
    LineChart,
    BarChart,
    PieChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    MarkLineComponent,
    MarkAreaComponent,
    GraphicComponent,
    TitleComponent,
    SVGRenderer,
]);

export interface EChartProps {
    option: EChartsCoreOption;
    /** Chart height in px; the chart always fills the available width. */
    height?: number;
    className?: string;
    /** Screen-reader name for the chart. */
    ariaLabel?: string;
    onEvents?: Record<string, (params: unknown) => void>;
}

/**
 * Thin React host for an ECharts instance.
 *
 * SVG renderer throughout: it stays crisp on any DPI, prints, and is
 * inspectable in tests. The instance is disposed and rebuilt whenever the
 * light/dark mode flips, because an ECharts theme is bound at init time — the
 * theme is re-registered from the live CSS tokens on each rebuild.
 */
const EChart: React.FC<EChartProps> = ({ option, height = 280, className, ariaLabel, onEvents }) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const chartRef = useRef<ECharts | null>(null);
    const mode = useChartMode();

    // Latest option, readable from the init effect without making the instance
    // tear down and rebuild every time the caller passes a new object.
    const optionRef = useRef(option);
    optionRef.current = option;

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;

        registerChartTheme(echarts, mode);
        const chart = echarts.init(host, chartThemeName(mode), {
            renderer: 'svg',
            // Both dimensions stay 'auto' so zrender measures the host on every
            // paint. A pixel value here would be latched into the painter's
            // opts and an argument-less resize() never clears it, freezing the
            // chart at its first-paint size.
            width: 'auto',
            height: 'auto',
        });
        chartRef.current = chart;
        // Draw immediately rather than waiting on the option effect: a theme
        // toggle re-inits an empty instance, and a caller whose `option`
        // identity is stable across that toggle would otherwise never re-apply
        // it and be left with a blank chart.
        chart.setOption(optionRef.current, { notMerge: true });

        const resize = () => chart.resize();
        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(resize);
            observer.observe(host);
        } else if (typeof window !== 'undefined') {
            window.addEventListener('resize', resize);
        }

        return () => {
            observer?.disconnect();
            if (typeof window !== 'undefined') window.removeEventListener('resize', resize);
            chart.dispose();
            chartRef.current = null;
        };
    }, [mode, height]);

    useEffect(() => {
        chartRef.current?.setOption(option, { notMerge: true });
    }, [option]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart || !onEvents) return undefined;
        const entries = Object.entries(onEvents);
        entries.forEach(([event, handler]) => chart.on(event, handler));
        // Detach by handler, not by event name — `off(event)` would also drop
        // handlers registered anywhere else on this instance.
        return () => {
            entries.forEach(([event, handler]) => chart.off(event, handler));
        };
        // Mirrors the init effect's deps: anything that rebuilds the instance
        // has to re-attach the handlers, or the fresh chart is left inert.
    }, [onEvents, mode, height]);

    return (
        <div
            ref={hostRef}
            className={className}
            style={{ width: '100%', height }}
            data-chart-mode={mode}
            role="img"
            aria-label={ariaLabel}
        />
    );
};

export default EChart;
